import Vue from 'vue';
import gql from 'graphql-tag';
import { eventManager } from '@breakmedia/prime-vue-event-manager';
import store from './store';
import basketModule from '../store/basket';
import shippingSelectorModule from '../store/shippingSelector';
import floatingBasketModule from '../store/floatingBasket';

import { loadProducts } from '@breakmedia/prime-vue-product/src/store/getProducts';

const basket = {
    sync() {
        return store
            .dispatch('syncBasketToServer', { forceMerge: false })
            .then(() => {
                const serverBasket = this.getServerBasket();
                if (serverBasket && serverBasket.items) {
                    const serverBasketItems = Object.values(serverBasket.items);
                    const serverBasketProductsIds = serverBasketItems.map(i => i.catalog_item_id);
                    if (serverBasketProductsIds.length) {
                        const productsPromises = [];
                        productsPromises.push(loadProducts({
                            ids: Object.values(Vue.$basket.getBasketProductsIds()),
                            fields: 'id',
                            priceParams: [],
                        }));
                        productsPromises.push(loadProducts({
                            ids: [...new Set(serverBasketProductsIds)],
                            fields: 'id',
                            priceParams: [],
                        }));
                        return Promise.all(productsPromises)
                            .then(([basketProducts, items]) => {
                                const localBasketProductsIds = basketProducts.map(i => i.id);
                                if (localBasketProductsIds.length < items.length) {
                                    return true;
                                } else {
                                    let mergeRequired = false;
                                    for (const item of items) {
                                        if (!localBasketProductsIds.includes(item.id)) {
                                            mergeRequired = true;
                                            break;
                                        }
                                    }
                                    return mergeRequired;
                                }
                            });
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            })
            .then(mergeRequired => {
                if (mergeRequired) {
                    Vue.$router.push({
                        name: 'basket-merge',
                    });
                }
            });
    },
    applyPricesSignatures(products) {
        const basketItems = this.getBasketItems();
        Object.keys(basketItems).forEach(hash => {
            const basketItem = basketItems[hash];
            const id = basketItem.catalog_item_id;
            products.forEach(product => {
                if (product.id === id || product.variantsIds.includes(id)) {
                    Object.values(product.merchant).forEach(merchant => {
                        if (merchant.price.hasOwnProperty(id)) {
                            if (merchant.id == basketItem.options.merchant) {
                                basketItem.price = merchant.price[id].price;
                                basketItem.rrp_price = merchant.price[id].original_price_inkl_vat;
                                basketItem.priceSignature = merchant.price[id].priceSignature;
                            }
                        } else {
                            basketItem.price = NaN;
                            basketItem.rrp_price = NaN;
                            basketItem.priceSignature = NaN;
                        }
                    });
                }
            });
        });
        store.commit('basketChanged');
        store.commit('setActualPricesLoaded');
    },
    getBasketItems(clone = false) {
        let result = {};

        if (clone) {
            Object.keys(store.state.items).forEach(hash => {
                result[hash] = Object.assign({}, store.state.items[hash]);
            });
        } else {
            result = store.state.items;
        }

        return result;
    },
    getBasketItem(hash) {
        return store.state.items[hash] ? store.state.items[hash] : {};
    },
    getBasketItemProduct(item) {
        return Vue.$store.state.Basket.products[item.product_id];
    },
    getBasketOptions() {
        return store.state.basketOptions;
    },
    getBasket() {
        return store.state.basket;
    },
    getConvertedLocalBasket() {
        return store.getters.getConvertedLocalBasket();
    },
    getServerBasket() {
        return store.getters.getConvertedServerBasket();
    },
    addBasketItem(item, silent = false, keepProduct = false) {
        this.resetLatestItems();

        const basketItem = Object.assign({}, item);
        basketItem.silent = silent;
        basketItem.keepProduct = keepProduct;

        return store.dispatch('addBasketItem', basketItem).then(result => {
            if (Vue.$store.state.FloatingBasket && silent === false) {
                const message = 'Produkt(e) in den Wartenkorb gelegt: ' + item.quantity + ' x ' + this.getItemTitle(item);
                Vue.$store.commit('FloatingBasket/setSuccessMessage', message);
                Vue.$store.commit('FloatingBasket/setShowFloatingBasket', true);
            }

            eventManager.$emit('basketAdd', {
                item: result,
                eventId: eventManager.getUniqueId('basketAdd'),
            });

            return result;
        });
    },
    updateBasketItem(item) {
        if (item && item.hash && this.getBasketItems()[item.hash]) {
            let existingItem = this.getBasketItems()[item.hash];
            let diffItem = Object.assign({}, item);

            diffItem.quantity = item.quantity - existingItem.quantity;
            if (diffItem.quantity > 0) {
                eventManager.$emit('basketAdd', {
                    item: diffItem,
                    eventId: eventManager.getUniqueId('basketAdd'),
                });
            } else if (diffItem.quantity < 0) {
                diffItem.quantity = Math.abs(diffItem.quantity);
                eventManager.$emit('basketRemove', {
                    item: diffItem,
                    eventId: eventManager.getUniqueId('basketRemove'),
                });
            }
        }

        return store.dispatch('updateBasketItem', item);
    },
    removeBasketItem(item) {
        if (typeof item === 'string') {
            item = this.getBasketItem(item);
        }

        eventManager.$emit('basketRemove', {
            item,
            eventId: eventManager.getUniqueId('productView'),
        });
        return store.dispatch('removeBasketItem', Object.assign({}, item));
    },
    removeBasketItemLocally(item) {
        if (typeof item === 'string') {
            item = this.getBasketItem(item);
        }

        eventManager.$emit('basketRemove', {
            item,
            eventId: eventManager.getUniqueId('productView'),
        });
        return store.dispatch('removeBasketItemLocally', item);
    },
    addBasketOption(option) {
        return store.dispatch('addBasketOption', option);
    },
    removeBasketOption(option) {
        return store.dispatch('removeBasketOption', option);
    },
    removeServerBasket() {
        store.commit('setServerBasket', {});
        store.dispatch('saveBasket');
    },
    mergeBaskets(forceMerge = true) {
        store.dispatch('saveBasket', {
            basket: store.getters.getMergedBasket(null, forceMerge),
        });
    },
    isBasketSyncAllowed() {
        return store.getters.isBasketSyncAllowed;
    },
    setShippingPrices(value) {
        store.commit('setShippingPrices', value);
    },
    getShippingPrices() {
        return store.state.shippingPrices;
    },
    setCountryId(value) {
        store.commit('setCountryId', value);
        store.commit('basketChanged');
    },
    getCountryId() {
        return store.state.countryId;
    },
    addDiscount(value) {
        store.commit('addDiscount', value);
    },
    getDiscounts() {
        return store.state.discounts;
    },
    deleteBasket() {
        if (Vue.$store.getters['Login/isUserLoggedIn']) {
            store.dispatch('deleteBasket');
        } else {
            store.commit('setBasket', {});
        }
    },
    getItemTitle(item) {
        const product = Vue.$store.state.Basket.products[item.product_id] || item.product;
        let title = '';
        if (product) {
            title = Vue.$store.getters['Product/getProductAttributeByLanguage'](product, 'product.name');
        }
        if (product && item.catalog_item_id != item.product_id) {
            const mapper = product.variants.mapper;
            const attributes = Vue.$store.getters['Product/getProductVariantAttributes'](product);
            Object.keys(mapper).forEach(indexname => {
                Object.keys(mapper[indexname]).forEach(value => {
                    Object.keys(mapper[indexname][value]).forEach(index => {
                        if (mapper[indexname][value][index] == item.catalog_item_id) {
                            title = title + ' ' + attributes[indexname].label + ' ' + value;
                        }
                    });
                });
            });
        }
        return title;
    },
    getBasketItemTotalPrice(item) {
        return this.getPriceData(item, null).price * item.quantity;
    },
    getPriceData(item, product = null) {
        if (product === null) {
            product = Vue.$store.state.Basket.products[item.product_id];
        }

        return product && product.merchant[item.options.merchant] && product.merchant[item.options.merchant].price[item.catalog_item_id] ?
            product.merchant[item.options.merchant].price[item.catalog_item_id] : {};
    },
    getBasketSubTotal() {
        let total = 0;
        const items = this.getBasketItems();
        Object.values(items).forEach(item => {
            total += this.getBasketItemTotalPrice(item);
        });
        return total;
    },
    getBasketTotalWithoutDiscount() {
        const basketSubtotal = Vue.$basket.getBasketSubTotal();
        const shippingTotal = Vue.$basket.getShippingTotal();
        const productsFeesTotal = Vue.$basket.getProductsFeesTotal();

        return basketSubtotal + shippingTotal + productsFeesTotal;
    },
    getTotalDiscount() {
        let totalDiscount = 0;
        Object.values(store.state.discounts).forEach(discount => {
            totalDiscount += discount;
        });

        return Math.min(totalDiscount, this.getBasketTotalWithoutDiscount());
    },
    getShippingTotal() {
        let total = 0;
        Object.values(store.state.shippingPrices).forEach(shippingPrice => {
            total += shippingPrice;
        });
        return total;
    },
    getBasketItemFees(item) {
        let fees = [];
        let product = item.product || Vue.$store.state.Basket.products[item.product_id];
        if (product) {
            for (let fee of Object.values(product.fees)) {
                if (this.feeApplicable(fee, item)) {
                    fees.push(fee);
                }
            }
        }

        return fees;
    },
    getProductsFees() {
        let fees = {};
        const basketItems = this.getBasketItems();
        Object.values(basketItems).forEach(item => {
            this.getBasketItemFees(item).forEach(fee => {
                if (!this.feeApplicable(fee, item)) {
                    return true;
                }
                fees[fee.name] = fee;
            });
        });

        return fees;
    },
    getProductsFeesTotals(items) {
        let feesTotal = {};

        if (!items) {
            items = Object.values(this.getBasketItems());
        }
        items.forEach(item => {
            const product = item.fees ? item : Vue.$store.state.Basket.products[item.product_id];
            if (product && product.fees) {
                Object.values(product.fees).forEach(fee => {
                    let feeValue;

                    if (!this.feeApplicable(fee, item)) {
                        return true;
                    }

                    if (fee.is_percent) {
                        const productPrice = item.price_vat || this.getPriceData(item).price;
                        feeValue = (fee.value * productPrice) / (100 + parseFloat(fee.value));
                    } else {
                        feeValue = fee.value;
                    }

                    const price = this.getPriceData(item);
                    if (price.VAT === 0) {
                        feeValue = feeValue / ((100 + parseFloat(price.original_VAT)) / 100);
                        feeValue = Math.round(feeValue * 100) / 100;
                    }

                    if (fee.per_product) {
                        if (feesTotal[fee.name] === undefined) {
                            feesTotal[fee.name] = 0;
                        }

                        feesTotal[fee.name] += item.quantity * feeValue;
                    } else {
                        if (feesTotal[fee.name] === undefined) {
                            feesTotal[fee.name] = feeValue;
                        }
                    }
                });
            }
        });
        return feesTotal;
    },
    feeApplicable(fee, item) {
        if (fee.type && fee.type.search('engraving') === 0) {
            let engravingProduct = false;
            if (item.buyOptions && item.buyOptions.length) {
                for (let buyOption of item.buyOptions) {
                    if (buyOption.name === 'engraving') {
                        for (let engravingProperty in buyOption.Values) {
                            if (buyOption.Values[engravingProperty].value.length) {
                                engravingProduct = true;
                                break;
                            }
                        }
                        break;
                    }
                }
            }
            if (!engravingProduct) {
                return false;
            }
        }
        return true;
    },
    getProductsFeesTotal(items) {
        let total = 0;
        const productsFeesTotals = this.getProductsFeesTotals(items);
        Object.values(productsFeesTotals).forEach(fee => {
            total += fee;
        });

        return total;
    },
    getBasketTotal(loadMissingProducts = false) {
        store.dispatch('recalculateBasketTotal', {loadMissingProducts});

        return store.state.basketTotal;
    },
    async getBasketTotalAsync(loadMissingProducts = false) {
        await store.dispatch('recalculateBasketTotal', {loadMissingProducts});

        return store.state.basketTotal;
    },
    getLastBasketChange() {
        return store.state.lastBasketChange;
    },
    isItemPickupable(item, merchantId) {
        let result = false;
        if (item && item.product && item.product.merchant) {
            const itemProps = item.product.merchant[merchantId];
            const merchantPickup = itemProps.data.attributes['allow_pickup'];
            const productPickup = itemProps.productAttributes[item.catalog_item_id]['system.pickupable'];
            result = merchantPickup || productPickup;
        }
        return result;
    },
    getLatestAddedItems() {
        return store.state.latestAddedItems;
    },
    resetLatestItems() {
        store.commit('resetLatestAddedItems');
    },
    isNotBuyableItem(item) {
        const product = Vue.$store.state.Basket.products[item.product_id];
        if (
            product &&
            product.hasOwnProperty('isNotBuyable') &&
            product.isNotBuyable.hasOwnProperty(item.catalog_item_id)
        ) {
            return product.isNotBuyable[item.catalog_item_id];
        }
        return false;
    },
    getItemHash(item) {
        return store.getters.getItemHash(item);
    },
    didBasketChanged(dateTime) {
        return dateTime > store.state.lastBasketChange;
    },
    didActualPricesLoaded() {
        return store.state.actualPricesLoaded;
    },
    getBasketProductsIds(basketItems = null) {
        const productIds = {};
        basketItems = basketItems ? basketItems : this.getBasketItems();
        Object.values(basketItems).forEach(basketItem => productIds[basketItem.hash] = basketItem.product_id);

        return productIds;
    },
    getPriceParams(basketItems = null, countryId = undefined, currency = undefined) {
        const merchantsItems = {};
        basketItems = basketItems ? basketItems : this.getBasketItems();

        Object.values(basketItems).forEach(basketItem => {
            const merchant = basketItem.options.merchant;

            if (merchantsItems.hasOwnProperty(merchant) === false) {
                merchantsItems[merchant] = [];
                merchantsItems[merchant].product_id = [];
                merchantsItems[merchant].quantity = {};
            }
            merchantsItems[merchant].product_id.push(basketItem.catalog_item_id.toString());
            merchantsItems[merchant].quantity[basketItem.catalog_item_id] = basketItem.quantity;
        });

        let country;
        if (countryId) {
            country = Vue.$country.getCountryBy('id', countryId) || undefined;
            if (country) {
                country = country.iso_2;
            }
        }

        const priceParams = [];
        Object.keys(merchantsItems).forEach(merchantId => {
            priceParams.push({
                product_id: merchantsItems[merchantId].product_id,
                country: country,
                merchant: merchantId,
                quantity: merchantsItems[merchantId].quantity,
                currency,
            });
        });

        return priceParams;
    },
    isBasketEmpty() {
        const basketItems = this.getBasketItems();
        return Object.values(basketItems).length === 0;
    },
    getLimitPerOrder(amount, isBasketField, product, catalogItemId, merchantId) {
        let limitPerOrder = 0;

        if (product) {
            const merchant = product.merchant[merchantId];
            if (merchant.limitPerOrder && merchant.limitPerOrder[catalogItemId]) {
                limitPerOrder = merchant.limitPerOrder[catalogItemId];
            }
            if (limitPerOrder > 0) {
                const basketItems = this.getBasketItems();
                let itemsInBasket = 0;
                if (!isBasketField) {
                    Object.values(basketItems).some(basketItem => {
                        if (
                            basketItem.catalog_item_id == catalogItemId &&
                            basketItem.options.merchant == merchantId
                        ) {
                            itemsInBasket = basketItem.quantity;
                            return true;
                        }
                    });
                }
                if (itemsInBasket + amount > limitPerOrder) {
                    return limitPerOrder;
                }
            }
        }

        return 0;
    },
    getMerchantizedItems(items = null) {
        if (!items) {
            items = Object.values(this.getBasketItems(true));
        }

        let merchantizedItems = {};

        for (const item of items) {
            const product = this.getBasketItemProduct(item);
            if (product) {
                item.product = product;
                if (item.options && item.options.merchant) {
                    if (!merchantizedItems[item.options.merchant]) {
                        merchantizedItems[item.options.merchant] = [];
                    }

                    merchantizedItems[item.options.merchant].push(item);
                }
            }
        }

        return merchantizedItems;
    },
    getMerchantTotal(merchantId, items = null) {
        let total = 0;
        let merchantizedItems = this.getMerchantizedItems(items);

        if (merchantizedItems[merchantId]) {
            for (const index of Object.keys(merchantizedItems[merchantId])) {
                let item = merchantizedItems[merchantId][index];
                let price = this.getBasketItemTotalPrice(item);
                total += price ? price : 0;
            }
        }

        return total;
    },
    isCoupon(product) {
        return product && product.type && product.type === 'coupon';
    }
};

export default {
    install: function(Vue) {
        if (!Vue.$store.state['Basket']) {
            Vue.$store.registerModule('Basket', basketModule);
        }
        if (!Vue.$store.state['ShippingSelector']) {
            Vue.$store.registerModule('ShippingSelector', shippingSelectorModule);
        }
        if (!Vue.$store.state['FloatingBasket']) {
            Vue.$store.registerModule('FloatingBasket', floatingBasketModule);
        }

        Vue.prototype.$basket = basket;
        Vue.$basket = basket;

        Vue.$graphql
            .query({
                query: gql`
                    query getBasketConfig {
                        getBasketConfig {
                            allowStorage
                            allowDatabase
                        }
                    }
                `,
            })
            .then(r => r.data.getBasketConfig)
            .then(basketConfig => {
                store.commit('setBasketConfig', basketConfig);
            });

        Vue.$store.watch(
            (state, getters) => getters['Login/isUserLoggedIn'],
            (value) => {
                if (value) {
                    store.dispatch('loadBasketProductsData').then(() => {
                        if (value) {
                            basket.sync();
                        }
                    });
                }
            },
        );
    },
};
